/**
 * Per-issue override menu for the drawer's Issues list, status-aware. Maps to
 * the same mutations the object-level actions use (annotate / transition) -
 * the parent wires onAction. Renders nothing for a state with no actions
 * (e.g. resolved). Split out of IssuesEntryList.tsx (one file per concern).
 */
import React, { useState } from 'react'
import {
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
} from '@mui/material'
import AssignmentTurnedInOutlinedIcon from '@mui/icons-material/AssignmentTurnedInOutlined'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline'
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined'
import RestartAltIcon from '@mui/icons-material/RestartAlt'
import MoreVertIcon from '@mui/icons-material/MoreVert'
import { poolwerxColors } from '../../../theme/colors'
import {
  BLOCKED_FG,
  MARKED_FIXED_FG,
  type IssuesChipEntry,
  type LifecycleStatus,
} from './issuesState'
import { TRANSITIONS } from './transitions'
import type { RowAction } from './transitions'

// Icon + outcome colour per kebab action. Mark fixed = teal AssignmentTurnedIn
// (actioned, awaiting sync confirmation - NEVER green, that's reserved for
// sync-confirmed resolved); Can't fix yet (block) = blocked-red; Ignore =
// neutral grey; Clear marked fixed / Remove block / Reopen are neutral.
const ACTION_ICONS: Record<RowAction, React.ReactNode> = {
  'mark-fixed': (
    <AssignmentTurnedInOutlinedIcon
      sx={{ fontSize: 18, color: MARKED_FIXED_FG }}
    />
  ),
  'unmark-fixed': (
    <AssignmentTurnedInOutlinedIcon
      sx={{ fontSize: 18, color: poolwerxColors.text.secondary }}
    />
  ),
  block: <PauseCircleOutlineIcon sx={{ fontSize: 18, color: BLOCKED_FG }} />,
  ignore: (
    <VisibilityOffIcon
      sx={{ fontSize: 18, color: poolwerxColors.text.tertiary }}
    />
  ),
  unblock: (
    <LockOpenOutlinedIcon
      sx={{ fontSize: 18, color: poolwerxColors.text.secondary }}
    />
  ),
  reopen: (
    <RestartAltIcon sx={{ fontSize: 18, color: poolwerxColors.text.secondary }} />
  ),
}

// Status-aware kebab items: the orthogonal fixed-marker annotation (open rows
// only - "Mark fixed" when unmarked, "Clear marked fixed" when already marked,
// never both) plus the transitions valid from the row's current state, sourced
// from the shared TRANSITIONS matrix the backend re-validates against.
// needs_review -> Can't fix yet / Ignore; blocked -> Remove block / Ignore;
// ignored -> Reopen for review / Can't fix yet; resolved -> none (immutable).
export const rowKebabItems = (
  rowState: LifecycleStatus,
  markedFixed = false,
): { action: RowAction; label: string }[] => {
  const items: { action: RowAction; label: string }[] = []
  if (rowState === 'needs_review' || rowState === 'blocked') {
    items.push(
      markedFixed
        ? { action: 'unmark-fixed', label: 'Clear marked fixed' }
        : { action: 'mark-fixed', label: 'Mark fixed' },
    )
  }
  for (const t of TRANSITIONS[rowState] ?? []) {
    items.push({ action: t.action, label: t.label })
  }
  return items
}

const IssuesRowKebab: React.FC<{
  entry: IssuesChipEntry
  rowState: LifecycleStatus
  disabled: boolean
  onAction: (entry: IssuesChipEntry, action: RowAction) => void
}> = ({ entry, rowState, disabled, onAction }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const close = () => setAnchorEl(null)
  const pick = (action: RowAction) => {
    close()
    onAction(entry, action)
  }
  const items = rowKebabItems(rowState, Boolean(entry.marked_fixed))
  if (items.length === 0) return null
  return (
    <>
      <IconButton
        size="small"
        aria-label="Issue actions"
        disabled={disabled}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        sx={{
          width: 32,
          height: 32,
          p: 0,
          color: 'text.secondary',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <MoreVertIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        // The drawer sits at zIndex.modal + 10; lift menus above it.
        sx={{ zIndex: (theme) => theme.zIndex.modal + 20 }}
      >
        {items.map((it) => (
          <MenuItem
            key={it.action}
            onClick={() => pick(it.action)}
            sx={{ py: '9px', px: '16px' }}
          >
            <ListItemIcon sx={{ minWidth: 30 }}>
              {ACTION_ICONS[it.action]}
            </ListItemIcon>
            <ListItemText primaryTypographyProps={{ fontSize: 14 }}>
              {it.label}
            </ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default IssuesRowKebab
